/**
 * Video Metadata Adapter
 * 
 * Handles MP4/MOV ilst atoms and Matroska tags for MKV/WEBM
 * with ffprobe integration for technical metadata.
 */

import { CanonicalMetadata, MetadataAdapter, MetadataProvenance } from '../canonical-schema';

export class VideoAdapter implements MetadataAdapter {
  private ffprobe: any;

  constructor() {
    // Only initialize FFProbe on server side
    if (typeof window === 'undefined') {
      this.initializeFFProbe();
    }
  }

  private async initializeFFProbe() {
    try {
      // Only import on server side
      if (typeof window === 'undefined') {
        const ffmpeg = await import('fluent-ffmpeg');
        this.ffprobe = ffmpeg.ffprobe;
      }
    } catch (error) {
      console.warn('FFProbe not available, falling back to basic extraction');
    }
  }

  async read(file: File): Promise<CanonicalMetadata> {
    const metadata: CanonicalMetadata = {};

    if (this.ffprobe) {
      return this.readWithFFProbe(file);
    } else {
      return this.readBasic(file);
    }
  }

  private async readWithFFProbe(file: File): Promise<CanonicalMetadata> {
    // FFProbe only works on server side, fall back to basic extraction in browser
    if (typeof window !== 'undefined') {
      return this.readBasic(file);
    }

    try {
      const buffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(buffer);
      
      // Create a temporary blob URL for ffprobe
      const blob = new Blob([uint8Array], { type: file.type });
      const url = URL.createObjectURL(blob);
      
      return new Promise((resolve, reject) => {
        this.ffprobe(url, (err: any, metadata: any) => {
          URL.revokeObjectURL(url);
          
          if (err) {
            console.error('FFProbe error:', err);
            resolve(this.readBasic(file));
            return;
          }

          const canonicalMetadata: CanonicalMetadata = {};
          const provenance: MetadataProvenance[] = [];

          // Basic metadata from format tags
          if (metadata.format?.tags) {
            const tags = metadata.format.tags;
            
            if (tags.title) {
              canonicalMetadata.title = tags.title;
              provenance.push({
                source: this.getTagSource(file),
                confidence: 0.9,
                timestamp: new Date(),
                software: 'FFProbe'
              });
            }

            if (tags.artist || tags.album_artist) {
              canonicalMetadata.creator = tags.artist || tags.album_artist;
            }

            if (tags.comment || tags.description) {
              canonicalMetadata.description = tags.comment || tags.description;
            }

            if (tags.genre) {
              canonicalMetadata.show = {
                ...canonicalMetadata.show,
                title: tags.genre // Using genre field for show genre
              };
            }

            if (tags.date) {
              canonicalMetadata.provenance = {
                created: new Date(tags.date),
                software: tags.software || tags.encoder
              };
            }

            // Custom fields from native tags
            const custom: Record<string, any> = {};
            Object.keys(tags).forEach(key => {
              if (!['title', 'artist', 'album_artist', 'comment', 'description', 'genre', 'date', 'software', 'encoder'].includes(key)) {
                custom[`video_${key}`] = tags[key];
              }
            });
            
            if (Object.keys(custom).length > 0) {
              canonicalMetadata.custom = custom;
            }
          }

          // Technical metadata from streams
          if (metadata.streams && metadata.streams.length > 0) {
            const videoStream = metadata.streams.find((s: any) => s.codec_type === 'video');
            const audioStream = metadata.streams.find((s: any) => s.codec_type === 'audio');

            canonicalMetadata.technical = {
              duration: metadata.format?.duration ? parseFloat(metadata.format.duration) : undefined,
              bitrate: metadata.format?.bit_rate ? parseInt(metadata.format.bit_rate) : undefined,
              container: metadata.format?.format_name,
              codec: videoStream?.codec_name,
              resolution: videoStream ? {
                width: videoStream.width,
                height: videoStream.height
              } : undefined,
              frameRate: videoStream?.r_frame_rate ? this.parseFrameRate(videoStream.r_frame_rate) : undefined,
              colorSpace: videoStream?.color_space,
              colorDepth: videoStream?.bits_per_raw_sample ? parseInt(videoStream.bits_per_raw_sample) : undefined,
              sampleRate: audioStream?.sample_rate ? parseInt(audioStream.sample_rate) : undefined,
              channels: audioStream?.channels
            };
          }

          // Show/Episode information (common in TV shows)
          if (metadata.format?.tags) {
            const tags = metadata.format.tags;
            
            if (tags.show || tags.series) {
              canonicalMetadata.show = {
                title: tags.show || tags.series,
                season: tags.season ? parseInt(tags.season) : undefined,
                episode: tags.episode ? parseInt(tags.episode) : undefined,
                network: tags.network,
                director: tags.director,
                writer: tags.writer
              };
            }
          }

          resolve(canonicalMetadata);
        });
      });
    } catch (error) {
      console.error('FFProbe read error:', error);
      return this.readBasic(file);
    }
  }

  private async readBasic(file: File): Promise<CanonicalMetadata> {
    const metadata: CanonicalMetadata = {
      title: file.name.replace(/\.[^/.]+$/, ""),
      technical: {
        container: file.type.split('/')[1].toUpperCase()
      }
    };

    return metadata;
  }

  async write(file: File, metadata: CanonicalMetadata): Promise<File> {
    // Note: Writing video metadata requires specialized tools
    // In a real implementation, you'd use:
    // - ExifTool for MP4/MOV files
    // - mkvtoolnix for Matroska files
    // - FFmpeg for general video metadata
    
    if (typeof window !== 'undefined') {
      throw new Error('Video metadata writing not available in browser environment. Use server-side tools.');
    }
    
    throw new Error('Video metadata writing not yet implemented. Use ExifTool or FFmpeg for video files.');
  }

  getSupportedExtensions(): string[] {
    return [
      '.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.m4v',
      '.3gp', '.ogv', '.mts', '.m2ts', '.ts', '.vob', '.asf', '.rmvb'
    ];
  }

  supports(file: File): boolean {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    return this.getSupportedExtensions().includes(extension);
  }

  private getTagSource(file: File): string {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (['.mp4', '.mov', '.m4v'].includes(extension)) {
      return 'mp4';
    } else if (['.mkv', '.webm'].includes(extension)) {
      return 'matroska';
    } else {
      return 'video';
    }
  }

  private parseFrameRate(frameRate: string): number {
    // Parse frame rate from FFProbe format (e.g., "30000/1001")
    const [numerator, denominator] = frameRate.split('/').map(Number);
    return denominator ? numerator / denominator : numerator;
  }
}

/**
 * MP4/MOV ilst Atom Mapping
 * Maps canonical metadata to MP4 ilst atoms
 */
export const MP4_VIDEO_ATOM_MAP = {
  title: '©nam',
  artist: '©ART',
  album: '©alb',
  year: '©day',
  genre: '©gen',
  comment: '©cmt',
  show: 'tvsh',
  season: 'tvsn',
  episode: 'tven',
  network: 'tvnn',
  director: '©dir',
  writer: '©wrt',
  cast: '©act'
};

/**
 * Matroska Tag Mapping
 * Maps canonical metadata to Matroska tag names
 */
export const MATROSKA_TAG_MAP = {
  title: 'TITLE',
  artist: 'ARTIST',
  album: 'ALBUM',
  year: 'DATE_RELEASED',
  genre: 'GENRE',
  comment: 'COMMENT',
  show: 'SHOW',
  season: 'SEASON_NUMBER',
  episode: 'EPISODE_NUMBER',
  network: 'NETWORK',
  director: 'DIRECTOR',
  writer: 'WRITTEN_BY',
  cast: 'ACTOR'
};
